/* EditEntryModal.jsx */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Autocomplete,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/system';
import { toast } from 'react-toastify';

const StyledPopper = styled('div')(({ theme }) => ({
  zIndex: 13001,
  '& .MuiPaper-root': {
    borderRadius: '8px',
    background: '#FFF',
    boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.32)',
    maxHeight: '300px',
    width: '300px',
    minWidth: '300px',
  },
  '& .MuiAutocomplete-listbox': {
    padding: '4px 0',
    maxHeight: '300px',
    width: '300px',
  },
  '& .MuiAutocomplete-option': {
    minHeight: '40px',
    padding: '4px 12px',
    fontSize: '14px',
  },
}));

const StyledListItem = styled('li')({
  '&:hover': { backgroundColor: '#F5F5F6', cursor: 'pointer', width: '100%' },
  padding: '4px 12px',
  minHeight: '32px',
  fontSize: '14px',
});

const EditEntryModal = ({
  open,
  onClose,
  onSave,
  loading,
  t,
  // Form state
  keyType,
  setKeyType,
  selectedSortCode,
  setSelectedSortCode,
  selectedAccountCard,
  setSelectedAccountCard,
  manualKey,
  setManualKey,
  value,
  setValue,
  isAmountPercentage,
  setIsAmountPercentage,
  // Options
  sortCodeOptions,
  accountCardOptions,
  inputPropsStyle,
  // New props for percentage calculation
  onGetSortCodeTotal,
  onGetAccountCardTotal,
  client,
  year,
  token,
  editingItem,
  isFromLedgerReport = false
}) => {
  const [editModalSortCodeTotal, setEditModalSortCodeTotal] = useState(0);
  const [editModalPercentage, setEditModalPercentage] = useState('');
  const [editModalCalculatedValue, setEditModalCalculatedValue] = useState('');
  const [loadingEditModalSortCodeTotal, setLoadingEditModalSortCodeTotal] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [originalValues, setOriginalValues] = useState(null); // Store original values to detect changes
  const fetchingRef = useRef(false); // Ref to prevent duplicate requests
  const lastFetchedSortCodeRef = useRef(null); // Ref to track last fetched SortCode

  // Memoized function to fetch SortCode total
  const fetchEditModalSortCodeTotal = useCallback(async () => {
    // Prevent duplicate requests
    if (fetchingRef.current) {
      return;
    }

    const useAccountCard = isFromLedgerReport && keyType === 'accountcard';
    const selectedKey = useAccountCard ? selectedAccountCard : selectedSortCode;
    
    if (!selectedKey) {
      return;
    }

    fetchingRef.current = true;
    setLoadingEditModalSortCodeTotal(true);
    try {
      const data = useAccountCard && onGetAccountCardTotal
        ? await onGetAccountCardTotal(client.caseId, client.accountantId, year, selectedKey, token)
        : await onGetSortCodeTotal(client.caseId, client.accountantId, year, selectedKey, token);
      if (data && data.sortCodeTotal !== undefined) {
        const total = data.sortCodeTotal || 0;
        setEditModalSortCodeTotal(total);
        lastFetchedSortCodeRef.current = selectedKey;
        // NEVER overwrite existing value when editing - only fetch the total for calculation
        // The existing value should always be preserved from editingItem
      }
    } catch (error) {
      console.error(`Error fetching ${useAccountCard ? 'account card' : 'sort code'} total for edit modal:`, error);
      setEditModalSortCodeTotal(0);
      lastFetchedSortCodeRef.current = null;
    } finally {
      setLoadingEditModalSortCodeTotal(false);
      fetchingRef.current = false;
    }
  }, [keyType, selectedSortCode, selectedAccountCard, isFromLedgerReport, onGetSortCodeTotal, onGetAccountCardTotal, client?.caseId, client?.accountantId, year, token]);

  // Fetch SortCode total when SortCode is selected
  useEffect(() => {
    // Only fetch if modal is open and not initializing
    if (!open || isInitializing) {
      return;
    }

    // Fetch when SortCode or AccountCard actually changes
    const useAccountCard = isFromLedgerReport && keyType === 'accountcard';
    const selectedKey = useAccountCard ? selectedAccountCard : selectedSortCode;
    if ((keyType === 'sortcode' || (isFromLedgerReport && keyType === 'accountcard')) && selectedKey && selectedKey !== lastFetchedSortCodeRef.current) {
      // Only reset percentage and calculated value if we're changing to a different sort code/account card
      // Don't reset during initial load (when lastFetchedSortCodeRef.current is null)
      const isChangingKey = lastFetchedSortCodeRef.current !== null;
      if (isChangingKey) {
        setEditModalPercentage('');
        setEditModalCalculatedValue('');
      }
      fetchEditModalSortCodeTotal();
    }
  }, [open, isInitializing, keyType, selectedSortCode, selectedAccountCard, isFromLedgerReport, fetchEditModalSortCodeTotal]);

  // Calculate value from percentage for edit modal (only when user changes percentage)
  useEffect(() => {
    // Only calculate if user is actively editing (not during initialization)
    // AND we have a percentage value entered by the user
    // Allow negative totals - change condition from > 0 to !== 0
    if (!isInitializing && !isAmountPercentage && editModalSortCodeTotal !== 0) {
      if (editModalPercentage && editModalPercentage.trim() !== '') {
        const percentageNum = parseFloat(editModalPercentage);
        if (!isNaN(percentageNum) && percentageNum > 0) {
          // Calculate: percentage × total (works with both positive and negative totals)
          const calculated = (percentageNum / 100) * editModalSortCodeTotal;
          const calculatedStr = calculated.toFixed(2);
          setEditModalCalculatedValue(calculatedStr);
          // Only update value state if it's different (to avoid unnecessary re-renders)
          if (value !== calculatedStr) {
            setValue(calculatedStr);
          }
        }
      } else {
        // When percentage is cleared/empty, reset calculated value to the original total
        const totalStr = editModalSortCodeTotal.toFixed(2);
        setEditModalCalculatedValue(totalStr);
        if (value !== totalStr) {
          setValue(totalStr);
        }
      }
    } else if (isAmountPercentage) {
      setEditModalCalculatedValue('');
      if (!isInitializing) {
        setEditModalPercentage('');
      }
    }
    // Don't overwrite existing value when percentage is empty or total changes
    // The existing value should be preserved from editingItem
  }, [editModalPercentage, editModalSortCodeTotal, isAmountPercentage, setValue, isInitializing, value]);

  // When editing item changes, populate form with existing values
  useEffect(() => {
    if (!open || !editingItem) {
      setIsInitializing(false);
      setEditModalPercentage('');
      setOriginalValues(null);
      fetchingRef.current = false;
      lastFetchedSortCodeRef.current = null;
      return;
    }

    setIsInitializing(true);
    
    // Reset fetch tracking when editing a new item
    fetchingRef.current = false;
    lastFetchedSortCodeRef.current = null;
    setEditModalSortCodeTotal(0); // Reset total when item changes
    
    // Store original values to detect changes later
    const original = {
      key: editingItem.key,
      internalKey: editingItem.internalKey,
      isInternalKey: editingItem.isInternalKey,
      value: editingItem.value,
      isAmountPercentage: editingItem.isAmountPercentage,
      percentage: editingItem.percentage,
      keyType: editingItem.isInternalKey && editingItem.internalKey 
        ? (isFromLedgerReport
            ? ((accountCardOptions || []).find(opt => opt.cardId === editingItem.internalKey) ? 'accountcard' : 'manual')
            : (sortCodeOptions.find(opt => opt.sortCodeId === editingItem.internalKey) ? 'sortcode' : 'manual'))
        : 'manual',
      selectedSortCode: editingItem.isInternalKey && editingItem.internalKey && !isFromLedgerReport
        ? editingItem.internalKey 
        : '',
      selectedAccountCard: editingItem.isInternalKey && editingItem.internalKey && isFromLedgerReport
        ? editingItem.internalKey 
        : '',
      manualKey: editingItem.isInternalKey && editingItem.internalKey
        ? (isFromLedgerReport
            ? ((accountCardOptions || []).find(opt => opt.cardId === editingItem.internalKey) 
                ? '' 
                : `${editingItem.internalKey} - ${editingItem.key}`)
            : (sortCodeOptions.find(opt => opt.sortCodeId === editingItem.internalKey) 
                ? '' 
                : `${editingItem.internalKey} - ${editingItem.key}`))
        : editingItem.key || ''
    };
    setOriginalValues(original);
    
    // Set the existing value and calculated value first (preserve DB value)
    if (editingItem.value) {
      const existingValue = editingItem.value;
      setValue(existingValue);
      if (!editingItem.isAmountPercentage) {
        // Preserve the existing calculated value from DB
        setEditModalCalculatedValue(existingValue);
      }
    }
    
    // Populate percentage if it exists in DB - do this after setting isAmountPercentage
    // to ensure the percentage mode is set correctly first
    if (editingItem.percentage !== null && editingItem.percentage !== undefined && !editingItem.isAmountPercentage) {
      // Set percentage after a small delay to ensure mode is set
      setTimeout(() => {
        setEditModalPercentage(editingItem.percentage.toString());
      }, 50);
    }
    
    // Mark initialization as complete after a short delay
    setTimeout(() => {
      setIsInitializing(false);
    }, 100);
  }, [open, editingItem, setValue, sortCodeOptions]);

  // Calculate percentage from existing value when SortCode/AccountCard total is available
  // Only calculate if percentage doesn't exist in DB and we're not initializing
  useEffect(() => {
    // Don't calculate during initialization - wait for it to complete
    if (isInitializing || !editingItem) {
      return;
    }
    
    const useAccountCard = isFromLedgerReport && keyType === 'accountcard';
    const selectedKey = useAccountCard ? selectedAccountCard : selectedSortCode;
    // Allow negative totals - change condition from > 0 to !== 0
    // Only calculate if we're in percentage mode and have the necessary data
    if (!editingItem.isAmountPercentage && editingItem.value && editModalSortCodeTotal !== 0 && 
        (keyType === 'sortcode' || (isFromLedgerReport && keyType === 'accountcard')) && selectedKey) {
      // Only calculate percentage if we don't already have one from DB AND we don't already have one in the input
      // This prevents overwriting the percentage that was set during initialization
      if ((editingItem.percentage === null || editingItem.percentage === undefined) && !editModalPercentage) {
        const valueNum = parseFloat(editingItem.value);
        // Allow both positive and negative values for calculation
        if (!isNaN(valueNum) && valueNum !== 0) {
          const calculatedPercentage = (valueNum / editModalSortCodeTotal) * 100;
          setEditModalPercentage(calculatedPercentage.toFixed(2));
        }
      }
    }
  }, [editingItem, editModalSortCodeTotal, keyType, selectedSortCode, selectedAccountCard, isFromLedgerReport, isInitializing, editModalPercentage]);

  const handleSave = () => {
    // For percentage mode, check if percentage is set instead of value
    const useAccountCard = isFromLedgerReport && keyType === 'accountcard';
    const selectedKey = useAccountCard ? selectedAccountCard : selectedSortCode;
    
    const hasValue = isAmountPercentage 
      ? value 
      : (editModalPercentage && editModalCalculatedValue);
    
    // Validation: check if we have required values
    // For sortcode: need selectedSortCode
    // For accountcard (when isFromLedgerReport): need selectedAccountCard
    // For manual: need manualKey
    const isValidKey = (keyType === 'sortcode' && selectedSortCode) ||
                       (keyType === 'accountcard' && isFromLedgerReport && selectedAccountCard) ||
                       (keyType === 'manual' && manualKey);
    
    if (!hasValue || !isValidKey) {
      console.log('Validation failed:', { hasValue, isValidKey, keyType, isFromLedgerReport, selectedSortCode, selectedAccountCard, manualKey });
      return;
    }

    // Check if there are any actual changes
    if (originalValues) {
      const useAccountCard = isFromLedgerReport && keyType === 'accountcard';
      const selectedKey = useAccountCard ? selectedAccountCard : selectedSortCode;
      
      const currentKey = (keyType === 'sortcode' || (isFromLedgerReport && keyType === 'accountcard'))
        ? (useAccountCard
            ? ((accountCardOptions || []).find(opt => opt.cardId === selectedAccountCard)?.name || '')
            : (sortCodeOptions.find(opt => opt.sortCodeId === selectedSortCode)?.sortCodeName || ''))
        : manualKey;
      
      const currentInternalKey = (keyType === 'sortcode' || (isFromLedgerReport && keyType === 'accountcard')) ? selectedKey : null;
      const currentIsInternalKey = keyType === 'sortcode' || (isFromLedgerReport && keyType === 'accountcard');
      
      const currentValue = isAmountPercentage ? value : editModalCalculatedValue;
      const currentPercentage = (!isAmountPercentage && editModalPercentage) ? parseFloat(editModalPercentage) : null;
      
      // Compare all fields
      const hasChanges = 
        currentKey !== originalValues.key ||
        currentInternalKey !== originalValues.internalKey ||
        currentIsInternalKey !== originalValues.isInternalKey ||
        currentValue !== originalValues.value ||
        isAmountPercentage !== originalValues.isAmountPercentage ||
        (currentPercentage !== null && originalValues.percentage !== null && 
         Math.abs(currentPercentage - parseFloat(originalValues.percentage)) > 0.01) ||
        (currentPercentage !== null && originalValues.percentage === null) ||
        (currentPercentage === null && originalValues.percentage !== null);
      
      if (!hasChanges) {
        // No changes made, don't send request
        toast.info(t('NoChangesToSave') || 'No changes to save.');
        onClose();
        return;
      }
    }

    // Ensure we use the correct value - for percentage mode, use calculated value
    const finalValueToUse = isAmountPercentage ? value : editModalCalculatedValue;
    
    // Pass percentage and calculated value if in percentage mode
    // IMPORTANT: When in amount mode (isAmountPercentage = true), always pass null for percentage to clear it
    const percentageToPass = isAmountPercentage 
      ? null 
      : (editModalPercentage ? parseFloat(editModalPercentage) : null);
    const calculatedValueToPass = (!isAmountPercentage && editModalCalculatedValue) ? editModalCalculatedValue : null;
    
    // Update the value state if needed (for percentage mode) before calling onSave
    if (!isAmountPercentage && editModalCalculatedValue && editModalCalculatedValue !== value) {
      setValue(editModalCalculatedValue);
      // Use setTimeout to ensure state is updated before parent reads it
      setTimeout(() => {
        onSave(percentageToPass, calculatedValueToPass);
      }, 0);
      return;
    }
    
    onSave(percentageToPass, calculatedValueToPass);
  };
  
  const handleKeyTypeChange = (newKeyType) => {
    setKeyType(newKeyType);
    setSelectedSortCode('');
    setSelectedAccountCard('');
    setManualKey('');
    setEditModalSortCodeTotal(0);
    setEditModalPercentage('');
    setEditModalCalculatedValue('');
    // When switching to manual key type, set to amount mode (percentage doesn't apply)
    if (newKeyType === 'manual') {
      setIsAmountPercentage(true);
    }
  };

  // Handle value type toggle (Amount/Percentage)
  const handleValueTypeToggle = (newMode) => {
    setIsAmountPercentage(newMode);
    // Don't clear percentage and calculated value when switching to amount mode - keep them
    // When switching to Amount mode, set value to the calculated value if available, otherwise use current value
    if (newMode) {
      // If we have a calculated value, use it; otherwise use the current value
      if (editModalCalculatedValue) {
        setValue(editModalCalculatedValue);
      }
      // If no calculated value but we have a sort code total, use that
      else {
        const useAccountCard = isFromLedgerReport && keyType === 'accountcard';
        const selectedKey = useAccountCard ? selectedAccountCard : selectedSortCode;
        if ((keyType === 'sortcode' || (isFromLedgerReport && keyType === 'accountcard')) && selectedKey && editModalSortCodeTotal !== 0) {
          const totalStr = editModalSortCodeTotal.toFixed(2);
          setValue(totalStr);
        }
      }
    }
    // When switching back to percentage mode, restore calculated value if percentage exists
    else {
      const useAccountCard = isFromLedgerReport && keyType === 'accountcard';
      const selectedKey = useAccountCard ? selectedAccountCard : selectedSortCode;
      
      // First, check if we have a percentage from the database (editingItem) or from previous input
      const percentageToUse = editModalPercentage || (editingItem && editingItem.percentage !== null && editingItem.percentage !== undefined ? editingItem.percentage.toString() : null);
      
      // If we have a percentage (from input or database), recalculate the value from it
      if (percentageToUse) {
        const percentageNum = parseFloat(percentageToUse);
        if (!isNaN(percentageNum) && percentageNum > 0 && editModalSortCodeTotal !== 0) {
          const calculated = (percentageNum / 100) * editModalSortCodeTotal;
          const calculatedStr = calculated.toFixed(2);
          setEditModalCalculatedValue(calculatedStr);
          setValue(calculatedStr);
          // Make sure percentage is set in the input field
          if (!editModalPercentage) {
            setEditModalPercentage(percentageToUse);
          }
        }
      }
      // If no percentage but we have current value and total, calculate percentage from current value
      else if (value && (keyType === 'sortcode' || (isFromLedgerReport && keyType === 'accountcard')) && selectedKey && editModalSortCodeTotal !== 0) {
        const valueNum = parseFloat(value);
        // Allow both positive and negative values for calculation
        if (!isNaN(valueNum) && valueNum !== 0) {
          const calculatedPercentage = (valueNum / editModalSortCodeTotal) * 100;
          setEditModalPercentage(calculatedPercentage.toFixed(2));
          setEditModalCalculatedValue(value);
        }
      }
      // If no value but we have total, set calculated value to total
      else if ((keyType === 'sortcode' || (isFromLedgerReport && keyType === 'accountcard')) && selectedKey && editModalSortCodeTotal !== 0) {
        const totalStr = editModalSortCodeTotal.toFixed(2);
        setEditModalCalculatedValue(totalStr);
        setValue(totalStr);
      }
      // Otherwise, just set calculated value to current value if it exists
      else if (value) {
        setEditModalCalculatedValue(value);
      }
    }
  };

  // Determine if we should use AccountCard or SortCode based on isFromLedgerReport
  const useAccountCard = isFromLedgerReport;
  const keyTypeForSelection = useAccountCard ? 'accountcard' : 'sortcode';
  const selectedKey = useAccountCard ? selectedAccountCard : selectedSortCode;
  const options = useAccountCard ? (accountCardOptions || []) : sortCodeOptions;
  
  const shouldShowTwoInputs = !isAmountPercentage && keyType === keyTypeForSelection && selectedKey;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      PaperProps={{
        style: {
          direction: 'rtl',
          zIndex: 13000
        }
      }}
      sx={{
        zIndex: 13000,
        '& .MuiDialog-container': {
          zIndex: 13000
        },
        '& .MuiBackdrop-root': {
          zIndex: 13000
        },
        '& .MuiDialog-paper': {
          overflow: 'visible'
        }
      }}
    >
      <DialogTitle>
        {t('EditEntry') || 'ערוך רשומה'}
      </DialogTitle>
      <DialogContent sx={{ overflow: 'visible', padding: '24px' }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Key Type Selector */}
          <FormControl size="small" style={{ minWidth: 120 }}>
            <InputLabel>{t('KeyType') || 'סוג מפתח'}</InputLabel>
            <Select
              value={keyType}
              onChange={(e) => handleKeyTypeChange(e.target.value)}
              label={t('KeyType') || 'סוג מפתח'}
              MenuProps={{
                disablePortal: false,
                style: { zIndex: 13001 },
                PaperProps: {
                  style: { zIndex: 13001 },
                },
              }}
            >
              {isFromLedgerReport ? (
                <MenuItem value="accountcard">{t('AccountCard') || 'כרטיס חשבון'}</MenuItem>
              ) : (
                <MenuItem value="sortcode">{t('SortCode') || 'קוד מיון'}</MenuItem>
              )}
              <MenuItem value="manual">{t('ManualText') || 'טקסט ידני'}</MenuItem>
            </Select>
          </FormControl>
          
          {/* Key Input */}
          <div style={{ width: 300 }}>
            {keyType === keyTypeForSelection ? (
              <Autocomplete
                options={options}
                getOptionLabel={(option) => useAccountCard 
                  ? `${option.cardId} - ${option.name}` 
                  : `${option.sortCodeId} - ${option.sortCodeName}`}
                value={useAccountCard
                  ? (accountCardOptions || []).find(opt => opt.cardId === selectedAccountCard) || null
                  : sortCodeOptions.find(opt => opt.sortCodeId === selectedSortCode) || null}
                onChange={(event, newValue) => {
                  const newKey = useAccountCard ? (newValue ? newValue.cardId : '') : (newValue ? newValue.sortCodeId : '');
                  const currentKey = useAccountCard ? selectedAccountCard : selectedSortCode;
                  
                  // Only reset if the key actually changed (not just clearing)
                  if (newKey !== currentKey) {
                    if (useAccountCard) {
                      setSelectedAccountCard(newKey);
                    } else {
                      setSelectedSortCode(newKey);
                    }
                    // Reset percentage and calculated value when sort code/account card changes
                    setEditModalPercentage('');
                    setEditModalCalculatedValue('');
                    // Reset the total to 0 so it will be fetched fresh
                    setEditModalSortCodeTotal(0);
                    lastFetchedSortCodeRef.current = null;
                  }
                }}
                noOptionsText=""
                disablePortal={false}
                ListboxProps={{
                  style: {
                    maxHeight: '300px',
                    padding: '4px 0',
                  }
                }}
                componentsProps={{
                  popper: {
                    style: { zIndex: 13001, width: '300px'},
                    placement: 'bottom-end',
                    modifiers: [
                      {
                        name: 'preventOverflow',
                        enabled: true,
                        options: {
                          altAxis: true,
                          altBoundary: true,
                          tether: false,
                          rootBoundary: 'viewport',
                        },
                      },
                    ],
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={useAccountCard ? (t('AccountCard') || 'כרטיס חשבון') : (t('SortCode') || 'קוד מיון')}
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                    }}
                    inputProps={{
                      ...params.inputProps,
                      style: { textAlign: 'right', direction: 'rtl' },
                    }}
                    size="small"
                    fullWidth
                  />
                )}
                renderOption={(props, option) => (
                  <Tooltip title={option.isEditable ? t('RightClickToEdit') || 'Right click to edit' : t('NotEditable') || 'Not editable'} placement="top">
                    <StyledListItem dir="rtl" {...props} >
                      {useAccountCard 
                        ? `${option.cardId} - ${option.name}` 
                        : `${option.sortCodeId} - ${option.sortCodeName}`}
                    </StyledListItem>
                  </Tooltip>
                )}
              />
            ) : (
              <TextField
                size="small"
                fullWidth
                placeholder={t('ManualKey') || 'מפתח ידני'}
                value={manualKey}
                onChange={(e) => setManualKey(e.target.value)}
                variant="outlined"
              />
            )}
          </div>
          
          {/* Value Type Toggle - Only show for SortCode or AccountCard entries */}
          {(keyType === 'sortcode' || (isFromLedgerReport && keyType === 'accountcard')) && (
            <FormControlLabel
              control={
                <Switch
                  checked={isAmountPercentage}
                  onChange={(e) => handleValueTypeToggle(e.target.checked)}
                  size="small"
                />
              }
              label={isAmountPercentage ? (t('Amount') || 'סכום') : (t('Percentage') || 'אחוז')}
              style={{ margin: 0, whiteSpace: 'nowrap' }}
            />
          )}
          
          {/* Value Inputs */}
          {shouldShowTwoInputs ? (
            // Percentage mode with SortCode: Show two inputs (percentage and calculated value)
            <>
              <div style={{ width: '60px', flexShrink: 0 }}>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  placeholder={t('Percentage') || 'אחוז'}
                  value={editModalPercentage}
                  onChange={(e) => {
                    const newPercentage = e.target.value;
                    // Allow empty string for clearing
                    if (newPercentage === '') {
                      setEditModalPercentage('');
                      return;
                    }
                    // Validate percentage range
                    const percentageNum = parseFloat(newPercentage);
                    if (!isNaN(percentageNum)) {
                      if (percentageNum < 0) {
                        toast.error(t('PercentageMustBeGreaterThanZero') || 'האחוז חייב להיות גדול או שווה ל-0');
                        setEditModalPercentage('');
                        return;
                      }
                      if (percentageNum > 100) {
                        toast.error(t('PercentageMustBeLessThanOrEqualToOneHundred') || 'האחוז חייב להיות קטן או שווה ל-100');
                        setEditModalPercentage('');
                        return;
                      }
                    }
                    setEditModalPercentage(newPercentage);
                  }}
                  variant="outlined"
                />
              </div>
              <div style={{ flex: 1, marginLeft: '8px' }}>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  placeholder={t('CalculatedValue') || 'ערך מחושב'}
                  value={editModalCalculatedValue}
                  disabled
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                  label={t('CalculatedValue') || 'ערך מחושב'}
                />
              </div>
            </>
          ) : (
            // Amount mode or manual key: Show single input
            <TextField
              size="small"
              style={{ minWidth: 150 }}
              type="number"
              placeholder={isAmountPercentage ? (t('Amount') || 'סכום') : (t('Percentage') || 'אחוז')}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              variant="outlined"
              label={isAmountPercentage ? (t('Amount') || 'סכום') : (t('Percentage') || 'אחוז')}
            />
          )}
        </div>
      </DialogContent>
      <DialogActions style={{ direction: 'rtl', padding: '16px 24px' }}>
        <Button onClick={onClose} color="error">
          {t('Cancel') || 'ביטול'}
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading || (() => {
            const useAccountCard = isFromLedgerReport && keyType === 'accountcard';
            const selectedKey = useAccountCard ? selectedAccountCard : selectedSortCode;
            if (isAmountPercentage) {
              return !value || ((keyType === 'sortcode' || (isFromLedgerReport && keyType === 'accountcard')) && !selectedKey) || (keyType === 'manual' && !manualKey);
            } else {
              return !editModalPercentage || !editModalCalculatedValue || ((keyType === 'sortcode' || (isFromLedgerReport && keyType === 'accountcard')) && !selectedKey) || (keyType === 'manual' && !manualKey);
            }
          })()}
          variant="contained"
          sx={{ bgcolor: '#304FFF' }}
        >
          {t('Update')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditEntryModal;

